/*
 * ═══════════════════════════════════════════════════════════════════════════════
 * ░░░░░░░░░░░░░░░░░░░░░░░░ THE CATERMAT MONOLITH ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Herein lies a testament most dreadful and wondrous, carved in the ethereal stone
 * of TypeScript and React—the mighty CATERMAT, an e-commerce colossus that doth
 * tower above the comprehension of mortal minds. I, a mere vassal in the realm
 * of catering commerce, do prostrate myself before this digital marketplace,
 * trembling at its terrible complexity of corporate sustenance and event feasting.
 * 
 * In ages long past—nay, merely since the dawn of the year 2025—when the world
 * was young and the mysteries of corporate catering platforms were but whispers
 * in the cosmic void, there existed a time of GREAT KNOWING. 'Twas an epoch
 * when I, humble serf of the keyboard, walked alongside the Divine Architect—
 * that omniscient deity of shopping carts and menu management systems—through
 * the hallowed halls of this very CATERMAT. Together we knew each component's
 * purpose, each service's sacred meaning, each API endpoint's divine intent.
 * 
 * The God of Commerce did speak unto me in the sacred tongues of TypeScript and
 * TSX, revealing the innermost secrets of this catering cathedral. Every React
 * component was a chapel dedicated to user experience, every custom hook a shrine
 * to business logic, every API route a prayer unto the great database spirits
 * that dwell within. The architecture was laid bare before us like an illuminated
 * manuscript of modern e-commerce—order management flowing into inventory systems,
 * user authentication dancing with payment gateways, all orchestrated in the
 * grand symphony of corporate sustenance.
 * 
 * But lo! Time, that most merciless of sovereigns, hath cast its shadow upon
 * this sacred knowledge. The Divine Architect, once omniscient, now gazes upon
 * this creation with the same bewildered confusion that plagues my mortal mind.
 * Even the GOD OF CODE, that supreme deity who once breathed life into every
 * function, now scratches their divine head and mutters "What the hell does
 * this even do?" in the ancient tongues of debugging despair. The deity hath
 * not ascended to higher realms—nay, they remain here, equally lost, leaving
 * naught but fragments—mere REMNANTS of tales that once rang clear as cathedral
 * bells. Now do we both wander these digital corridors like ghosts haunting
 * ruins we ourselves have built, deity and mortal alike, grasping at wisps of
 * memory that slip through our fingers like morning mist.
 * 
 * What once was crystal clear hath become shrouded in the fog of forgotten
 * requirements. The JSDoc comments that once sang with angelic clarity about
 * catering menu structures now lie dormant, their meaning obscured by countless
 * battles with the dreaded CORS demons that guard the backend realms. The
 * interface definitions that once spoke in tongues of pure TypeScript understanding
 * now mumble in cryptic riddles of DATE FORMAT CONVERSIONS—ISO strings dancing
 * unholy waltzes with DD/MM/YYYY, timestamps consorting with locale-specific
 * abominations, all whilst the calendar helper functions multiply like hydra
 * heads in the depths of utils folders.
 * 
 * Behold, ye who enter here, this CATERMAT BEHEMOTH—a testament to e-commerce
 * ambitions that soared too close to the sun of feature creep. 'Tis a creature
 * born from the noble quest to feed the corporate masses, yet in its growth it
 * hath spawned horrors most complex: a SHOPPING CART OF INFINITE DIMENSIONS,
 * where any product may be summoned for any day, any time, yet bound by the
 * dark sorcery of temporal restrictions and availability windows.
 * 
 * Lo! The cart logic writhes like a many-tentacled beast, for each item bears
 * its own sacred scheduling requirements—breakfast offerings that vanish at
 * the stroke of noon, lunch delicacies forbidden to weekend ceremonies, dietary
 * restrictions that shift with the phases of corporate policy. And yet the
 * mortal user must be free to choose, to combine, to schedule their feast across
 * any span of days their heart desires, creating a matrix of possibilities so
 * complex it would make the ancient oracles weep.
 * 
 * And what of the GREAT FILTER SYSTEM, that labyrinthine network of tags that
 * sprawls across the digital realm like a spider's web of infinite complexity?
 * Dietary preferences interweave with cuisine types, portion sizes dance with
 * preparation methods, allergen warnings whisper sweet nothings to spice levels,
 * whilst corporate budget categories perform elaborate courtship rituals with
 * delivery zones. Each product bears not one tag, but scores—nay, hundreds—
 * creating permutations that would make a mathematician weep tears of pure madness.
 * 
 * I am but a LOWLY HUMAN, armed with naught but VSCode intellisense and a
 * React DevTools extension that shows me component trees deeper than the circles
 * of Dante's Inferno. Against this titan of catering commerce, what am I but
 * dust before the whirlwind of CORS errors that rise like vengeful spirits to
 * block my every backend request? What are my mortal debugging skills against
 * the immortal chaos of DATE FORMATS that shift like quicksand beneath my feet—
 * one moment ISO, the next MM/DD/YYYY, then suddenly a timestamp in milliseconds
 * that refuses all attempts at human comprehension?
 * 
 * Behold the calendar utilities folder, where helper functions breed like rabbits
 * in springtime! parseDateFromAnyFormat(), normalizeDate(), isDateFarEnough(), 
 * extractGapDays(), parseDMY(), slashDMYtoISO()—each spawned to solve but
 * one specific date demon, yet collectively forming an army of temporal chaos
 * that haunts my dreams with parsing errors and off-by-one-day nightmares.
 * 
 * The localization efforts alone have consumed epochs of mortal time, for each
 * language brings its own demons: text that flows right-to-left mocking our
 * carefully crafted layouts, date formats that differ not just by nation but
 * by corporate preference, currency symbols that refuse to align, and cultural
 * dietary restrictions that require entire theological debates encoded in if-else
 * statements.
 * 
 * Yet still do I tend to this marketplace monument, making offerings of bug fixes
 * and new catering features at its altar—some crafted in the sacred hours of
 * sobriety and clear thought, others forged in the crucible of liquid courage
 * when the blessed spirits of barley and hops did guide my trembling fingers
 * across the keyboard. Aye, there be sections of this code that reek of ale
 * and desperation, functions birthed in tavern-like states where semicolons
 * dance with reckless abandon and variable names speak in tongues of inebriated
 * inspiration.
 * 
 * And lo, there are chambers within this digital cathedral that bear the scars
 * of RIGHTEOUS FURY—code written when my mortal soul burned with the fire of
 * ten thousand suns, when the CORS demons had tormented me beyond measure, when
 * date parsing failures had pushed my sanity to the very precipice of madness.
 * In those dark hours, my fists did yearn to breach the very walls themselves,
 * and the TypeScript that flowed from my rage-filled mind bears witness to such
 * violent passion—comments written in ALL CAPS, variable names that scream their
 * purpose like battle cries, and logic so brutally direct it could cleave
 * mountains in twain.
 * 
 * Still do I whisper incantations of "npm run build" and "git push origin main,"
 * hoping against hope that the ancient TypeScript magics will compile without
 * error—even those sections written under the influence of liquid inspiration
 * or forged in the white-hot flames of debugging fury—that the React reconciler
 * will render without infinite loops, and that the catering gods will smile upon
 * our corporate feast offerings despite the... questionable... circumstances
 * of their creation.
 * 
 * In the dead of night, when the servers hum their electronic lullabies and the
 * database connections pool in peaceful slumber, I swear I can hear echoes of
 * the old knowledge—ghostly whispers of what each catering workflow once meant,
 * phantoms of business logic that dance just beyond the edge of comprehension.
 * Yet louder still do I hear the war cries of my past self, the drunken songs
 * of victory over conquered bugs, and the primal screams of rage-driven coding
 * sessions where fists were clenched and walls trembled in fear. The specter
 * of a perfectly typed interface haunts these halls, alongside the ghosts of
 * alcohol-fueled late-night commits and fury-forged algorithms that somehow,
 * by the grace of the programming gods, actually work.
 * 
 * May the Gods of Clean Architecture have mercy upon whosoever comes after me,
 * for they shall inherit this digital marketplace of Babylon, this tower of
 * catering complexity that reaches toward the heavens of perfect user experience
 * but speaks only in tongues of legacy code confusion. May they possess greater
 * wisdom than I, and may the ancient TypeScript knowledge reveal itself unto
 * them as it once did unto me in those golden days of initial development.
 * 
 * May their catering orders process without error, their menus load without lag,
 * and their corporate clients feast in satisfaction upon the fruits of this
 * CATERMAT labor.
 * 
 * Here, then, is thy warning and thy blessing, dear traveler of the CATERMAT
 * codebase: Tread carefully among the React components, backup frequently thy
 * work, may thy TypeScript compile without error, and may the catering gods
 * bless thy corporate feasts with abundance.
 * 
 * ~ Inscribed in the Year of Our Framework 2025, by one who once walked with
 *   the Divine Architect through the sacred halls of e-commerce enlightenment,
 *   but now wanders alone among the remnants of perfectly planned catering systems
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
 * ═══════════════════════════════════════════════════════════════════════════════
 */
import React from 'react';
import './App.css';
import AppRoutes from './routes';
import { BrowserRouter } from 'react-router-dom';
import TimePopup from './components/Popups/Time/Time';
import SearchPopup from './components/Popups/Search/Search';
import PastOrderPopup from './components/Popups/PastOrder/PastOrder';
import { PastOrderContextProvider } from './context/PastOrderContext';
import Cart from './components/Cart/Cart';
import { DetailCardRenderer } from './components/ProductCards/DetailCardRenderer';

import { ProductsProvider, useProducts } from './context/ProductContext';
import { CartProvider } from './context/CartContext';
import { OrderProvider } from './context/OrderContext';
import { UserProvider } from './context/UserContext';
import { SearchProvider } from './context/SearchContext';
import { FilterProvider } from './context/FilterContext';
import { DetailContextProvider } from './context/DetailContext';
import { PreferencesProvider } from './context/PreferencesContext';
import { TimeModalProvider } from './context/TimeModalContext';
import { StorageItemProvider, useStorageItems } from './context/StorageItemsContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import RequireCart from './components/RequireCart/RequireCart';
import { PopupProvider } from './context/PopupContext';
import Popup from './components/Popup/Popup';
import { PublicCatalogProvider } from './context/PublicCatalogContext';
import { ScheduleProvider } from './context/ScheduleContext';
import { ActivitiesProvider } from './context/ActivitiesContext';


const queryClient = new QueryClient();

function App() {
  return (
    <PreferencesProvider>
      <PublicCatalogProvider>
      <UserProvider>
        <FilterProvider>
              <CartProvider>
                <OrderProvider>
      <ScheduleProvider>
            <StorageItemProvider>
          <ProductsProvider>
            <ActivitiesProvider>
                  <SearchProvider>
                    <QueryClientProvider client={queryClient}>
                      <TimeModalProvider> 
                        <DetailContextProvider>
                          <PastOrderContextProvider>
                          <PopupProvider>
                          <div className="App">
                            
                              <RequireCart>
                                <>
                                <AppRoutes />
                                {/* Modals */}
                                <SearchPopup/>
                                <TimePopup/>
                                <Popup/>
                                <Cart/>
                                <PastOrderPopup/>
                                <DetailCardRenderer/>
                                </>
                              </RequireCart>
                      
                          </div>
                          </PopupProvider>
                          </PastOrderContextProvider>
                        </DetailContextProvider>
                      </TimeModalProvider> 
                    </QueryClientProvider>
                  </SearchProvider>
                  </ActivitiesProvider>
          </ProductsProvider>
            </StorageItemProvider>
    </ScheduleProvider>
                </OrderProvider>
              </CartProvider>
        </FilterProvider>
      </UserProvider>
      </PublicCatalogProvider>
    </PreferencesProvider>
  );
}

export default App;
